import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import type { ReactNode } from 'react'

function ExampleDialog({
  link,
  title,
  description,
  children,
}: {
  link: string
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="text-sm text-foreground underline">
          {link}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">{children}</div>
        <DialogFooter>
          <DialogClose asChild>
            <SecondaryButton size="sm">Close</SecondaryButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Heading({ children }: { children: string }) {
  return <h3 className="text-sm font-semibold text-foreground">{children}</h3>
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-muted-foreground">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  )
}

const FEEDBACK_EXAMPLES = [
  'Sales: investors see your channel as unproven. Focus on showing one channel that wins customers.',
  'Unit economics: investors focus on what each customer brings in.',
  'Customers: a first paying customer, with a date, could bring investors back.',
  'Profile: investors looked for churn and found nothing. Adding it answers them.',
]

const FEEDBACK_PRIVACY = [
  'Never your name, firm, title or words. We write all feedback ourselves, and your thesis and searches across all startups never feed it.',
  'Feedback waits 24 hours. If you follow that startup, or the founder can see your name or firm another way, it waits until another firm the founder cannot name points to the same thing.',
  'Conditions lose every detail: a founder may read “a revenue milestone”, never your number. Follow conditions count only once three firms follow the startup.',
  'Nothing you do later removes feedback once it shows, so a later action never confirms a guess.',
  'Switch it off any time. You stop counting at once, including feedback still waiting.',
]

export function FeedbackExampleDialog() {
  return (
    <ExampleDialog
      link="See what founders see"
      title="What founders see"
      description="Anonymous feedback like this helps founders focus on what will strengthen their business."
    >
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          For example
        </p>
        <Bullets items={FEEDBACK_EXAMPLES} />
      </div>
      <div className="space-y-2">
        <Heading>How we keep you private</Heading>
        <Bullets items={FEEDBACK_PRIVACY} />
      </div>
      <p className="text-muted-foreground">
        Founders see that you follow. Your condition stays private.
      </p>
    </ExampleDialog>
  )
}

export function LookupsExampleDialog() {
  const steps = [
    'You ask your assistant about Norvale, a startup that is not on SeedForge.',
    'We keep two things for 90 days: the name Norvale and its website, norvale.io.',
    'Your Discovery watchdog learns from it and suggests similar startups on SeedForge.',
  ]
  return (
    <ExampleDialog
      link="See an example"
      title="How remembered startups work"
      description="An example of what happens when your assistant looks for a startup that is not on SeedForge."
    >
      <ol className="space-y-3">
        {steps.map((step, i) => (
          <li key={step} className="flex items-start gap-3 text-foreground">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
              {i + 1}
            </span>
            <span className="pt-0.5">{step}</span>
          </li>
        ))}
      </ol>
      <div className="space-y-2">
        <Heading>Your privacy</Heading>
        <Bullets
          items={[
            'Your searches stay private and are not shared with anyone outside SeedForge.',
            'Switch it off any time, and we stop remembering new ones.',
          ]}
        />
      </div>
    </ExampleDialog>
  )
}
